import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import MonitoringStatusBadge from '../ui/MonitoringStatusBadge'
import ActivationBanner from '../ui/ActivationBanner'
import { useOnboarding } from '../../contexts/OnboardingContext'
import OnboardingPanel from '../onboarding/OnboardingPanel'
import {
  HomeIcon,
  FolderIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CpuChipIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

interface DashboardLayoutProps {
  children: React.ReactNode
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false) // Mobile sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false) // Desktop collapse state
  const [isDesktop, setIsDesktop] = useState(false) // Track if we're on desktop
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { state: onboardingState, togglePanel } = useOnboarding()

  // Check if user is administrator
  const isAdmin = user?.roles?.includes('administrator') || false

  // Fetch pending approvals count
  const { data: pendingApprovalsData } = useQuery({
    queryKey: ['pendingApprovalsCount'],
    queryFn: () => apiClient.getPendingApprovals({ per_page: 1 }),
    staleTime: 30000,
    refetchInterval: 60000, // Refresh every minute
  })

  const pendingCount = pendingApprovalsData?.pagination?.total || 0

  // Navigation items - some are admin-only
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Projects', href: '/projects', icon: FolderIcon },
    { name: 'Opportunities', href: '/opportunities', icon: ChatBubbleLeftRightIcon },
    { name: 'Approvals', href: '/approvals', icon: CheckCircleIcon, badge: pendingCount > 0 ? `${pendingCount}` : undefined, badgeColor: 'bg-yellow-500' },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
    { name: 'Intelligence', href: '/intelligence', icon: CpuChipIcon, badge: 'Phase 2' },
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    // Admin-only items
    ...(isAdmin ? [
      { name: 'Cron Monitoring', href: '/dashboard/cron-monitoring', icon: ClockIcon, badge: 'Admin', adminOnly: true },
    ] : []),
  ]

  // Fetch monitoring status for the badge
  const { data: monitoringStatus } = useQuery({
    queryKey: ['monitoringStatus'],
    queryFn: () => apiClient.getMonitoringStatus(),
    staleTime: 30000,
    refetchInterval: 60000, // Refresh every minute
  })

  // Load collapsed state from localStorage on mount and check screen size
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    if (saved !== null) {
      setSidebarCollapsed(JSON.parse(saved))
    }

    // Check initial screen size
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  // Navigation item component - different behavior for mobile vs desktop
  const NavItem = ({ item, isActive, isMobile = false }: { item: typeof navigation[0], isActive: boolean, isMobile?: boolean }) => (
    <li>
      <Link
        to={item.href}
        className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-all duration-200 ${
          isActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-700 hover:text-blue-700 hover:bg-blue-50'
        } ${!isMobile && sidebarCollapsed ? 'justify-center' : ''}`}
      >
        <item.icon
          className={`h-6 w-6 shrink-0 transition-colors ${
            isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-blue-700'
          }`}
        />
        {isMobile ? (
          // Mobile: Always show text
          <span className="overflow-hidden whitespace-nowrap">
            {item.name}
          </span>
        ) : (
          // Desktop: Animated collapse
          <AnimatePresence>
            {!sidebarCollapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden whitespace-nowrap"
              >
                {item.name}
              </motion.span>
            )}
          </AnimatePresence>
        )}
      </Link>
    </li>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="relative z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />

          <div className="fixed inset-0 flex">
            <div className="relative mr-16 flex w-full max-w-xs flex-1">
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  className="-m-2.5 p-2.5"
                  onClick={() => setSidebarOpen(false)}
                >
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>

              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                <div className="flex h-16 shrink-0 items-center">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">RM</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">Redd Miner</span>
                  </div>
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul className="-mx-2 space-y-1">
                        {navigation.map((item) => {
                          const isActive = location.pathname === item.href
                          return <NavItem key={item.name} item={item} isActive={isActive} isMobile={true} />
                        })}
                      </ul>
                    </li>
                  </ul>
                </nav>

                {/* Mobile profile section */}
                <div className="flex items-center gap-x-4 px-2 py-3 text-sm font-semibold leading-6 text-gray-900 border-t border-gray-200">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Profile" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  )}
                  <Link to="/settings?tab=profile" className="flex-1">
                    <span className="block hover:text-blue-600 transition-colors">{user?.display_name || user?.username}</span>
                    <span className="text-xs text-gray-500">Your profile</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Logout"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar - Only render on desktop */}
      {isDesktop && (
        <motion.div
          className="fixed inset-y-0 z-50 flex flex-col"
          animate={{
            width: sidebarCollapsed ? '5rem' : '18rem' // 80px collapsed, 288px expanded
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
        <div className="flex grow flex-col overflow-hidden border-r border-gray-200 bg-white">
          {/* Header with logo only */}
          <div className="flex h-16 shrink-0 items-center px-4">
            <div className="flex items-center space-x-2 overflow-hidden">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">RM</span>
              </div>
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-xl font-bold text-gray-900 whitespace-nowrap overflow-hidden"
                  >
                    Redd Miner
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Collapse/Expand button - positioned between logo and menu */}
          <div className="px-4 pb-2">
            <button
              onClick={toggleSidebar}
              className={`p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-all duration-200 ${
                sidebarCollapsed ? 'w-full flex justify-center' : 'ml-auto flex'
              }`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-5 w-5" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col px-4">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href
                    return <NavItem key={item.name} item={item} isActive={isActive} />
                  })}
                </ul>
              </li>
            </ul>
          </nav>

          {/* Profile section */}
          <div className="px-4 pb-4">
            <div className={`flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-900 transition-all duration-200 ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}>
              {sidebarCollapsed ? (
                <Link to="/settings?tab=profile" className="flex items-center gap-x-2">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Profile" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  )}
                </Link>
              ) : (
                <Link to="/settings?tab=profile" className="flex items-center gap-x-3 flex-1 min-w-0">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Profile" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <UserCircleIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                  )}
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    className="flex-1 min-w-0 overflow-hidden"
                  >
                    <div className="truncate hover:text-blue-600 transition-colors">{user?.display_name || user?.username}</div>
                    <div className="text-xs text-gray-500 truncate">Your profile</div>
                  </motion.div>
                </Link>
              )}

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all duration-200"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
      )}

      {/* Main content area */}
      <motion.div
        className="lg:pl-0"
        animate={{
          paddingLeft: isDesktop ? (sidebarCollapsed ? '5rem' : '18rem') : '0rem'
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {/* Top navigation */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          {/* Mobile menu button */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="relative flex flex-1">
              {/* Page title will be handled by individual pages */}
            </div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Monitoring Status Badge */}
              {monitoringStatus && (
                <MonitoringStatusBadge
                  status={monitoringStatus.status}
                  nextRun={monitoringStatus.next_run}
                />
              )}

              {/* Onboarding Bell Icon with Badge */}
              <button
                type="button"
                onClick={togglePanel}
                className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500 transition-colors relative"
              >
                <BellIcon className="h-6 w-6" />
                {!onboardingState.isComplete && onboardingState.completedSteps < onboardingState.totalSteps && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                    {onboardingState.totalSteps - onboardingState.completedSteps}
                  </span>
                )}
              </button>

              <div className="h-6 w-px bg-gray-200" />


              <Link to="/settings?tab=profile" className="flex items-center gap-x-2">
                <span className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                  {user?.display_name || user?.username}
                </span>
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="Profile" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Activation Banner */}
        <ActivationBanner />

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </motion.div>

      {/* Onboarding Panel */}
      <OnboardingPanel />
    </div>
  )
}

export default DashboardLayout